import { useCallback, useReducer } from "react";
import { append, includes, pluck, without } from "ramda";

const actionTypes = {
  update_sort: "update_sort",
  update_page: "update_page",
  update_selected: "update_selected",
  update_edit: "update_edit",
};

const useTable = (callbacks = {}, initialState = {}) => {
  const tableReducer = (state, action) => {
    switch (action.type) {
      case actionTypes.update_sort: {
        return {
          ...initialState,
          sort: { property: action.property, order: action.order },
        };
      }
      case actionTypes.update_page: {
        return { ...initialState, page: action.page };
      }
      case actionTypes.update_selected: {
        return { ...initialState, selected: action.selected };
      }
      default: {
        throw new Error(`Unhandled type in mediaTableReducer: ${action.type}`);
      }
    }
  };
  const [state, dispatch] = useReducer(tableReducer, initialState);

  const handleRequestSort = useCallback(
    property => {
      const direction =
        state.sort.property !== property
          ? state.sort.order
          : state.sort.order === "desc"
          ? "asc"
          : "desc";

      dispatch({
        type: actionTypes.update_sort,
        property,
        order: direction,
      });
      callbacks.onSortChange &&
        callbacks.onSortChange({
          property,
          order: direction,
        });
    },
    [state, callbacks],
  );

  const handleChangePage = useCallback(
    page => {
      dispatch({
        type: actionTypes.update_page,
        page,
      });
      callbacks.onPageChange && callbacks.onPageChange(page);
    },
    [callbacks],
  );

  const handleRowClick = useCallback(
    id => {
      const updated = includes(id, state.selected)
        ? without([id], state.selected)
        : append(id, state.selected);

      dispatch({
        type: actionTypes.update_selected,
        selected: updated,
      });
      callbacks.onSelectChange && callbacks.onSelectChange(updated);
    },
    [state, callbacks],
  );

  const handleSelectAllClick = useCallback(
    (target, itemList) => {
      if (target.checked) {
        const updated = pluck("id", itemList);
        dispatch({
          type: actionTypes.update_selected,
          selected: updated,
        });
        callbacks.onSelectChange && callbacks.onSelectChange(updated);
      } else {
        dispatch({
          type: actionTypes.update_selected,
          selected: [],
        });
        callbacks.onSelectChange && callbacks.onSelectChange([]);
      }
    },
    [callbacks],
  );

  return {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
    handleSelectAllClick,
  };
};

export { useTable, actionTypes };
